import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatIDR, formatDate } from "@/lib/api";

/**
 * Cetak Nota Titipan untuk penyaluran (konsinyasi).
 * dist: { id, store_name, date, notes, items, total }
 */
export function printConsignmentNote(dist) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header brand
  doc.setFillColor(4, 120, 87);
  doc.rect(0, 0, pageWidth, 70, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("PROJECT HOTTO", 40, 32);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Sistem Pencatatan Stok & Kas", 40, 50);

  // Doc title
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("NOTA TITIPAN BARANG", pageWidth - 40, 32, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text(`No: ${(dist.id || "").slice(0, 8).toUpperCase()}`, pageWidth - 40, 50, { align: "right" });

  // Meta
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(9);
  doc.text("DITITIPKAN KEPADA", 40, 105);
  doc.text("TANGGAL", pageWidth - 200, 105);

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(dist.store_name || "-", 40, 125);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(formatDate(dist.date), pageWidth - 200, 125);

  // Items table
  autoTable(doc, {
    startY: 155,
    head: [["#", "Produk", "Qty", "Harga Titip", "Subtotal"]],
    body: dist.items.map((it, i) => [
      i + 1,
      it.product_name,
      `${it.qty} ${it.unit || "pcs"}`,
      formatIDR(it.unit_price),
      formatIDR(it.subtotal),
    ]),
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontSize: 10, fontStyle: "bold" },
    bodyStyles: { fontSize: 10, textColor: [30, 41, 59] },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      2: { halign: "right", cellWidth: 60 },
      3: { halign: "right", cellWidth: 100 },
      4: { halign: "right", cellWidth: 100, fontStyle: "bold" },
    },
    margin: { left: 40, right: 40 },
    theme: "grid",
  });

  const afterY = doc.lastAutoTable.finalY + 15;

  // Total box
  doc.setFillColor(240, 253, 244);
  doc.rect(pageWidth - 240, afterY, 200, 40, "F");
  doc.setTextColor(4, 120, 87);
  doc.setFontSize(9);
  doc.text("TOTAL NILAI TITIPAN", pageWidth - 230, afterY + 15);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(formatIDR(dist.total), pageWidth - 50, afterY + 30, { align: "right" });

  // Notes
  if (dist.notes) {
    doc.setTextColor(100, 116, 139);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Catatan:", 40, afterY + 15);
    doc.setTextColor(30, 41, 59);
    doc.text(doc.splitTextToSize(dist.notes, 250), 40, afterY + 30);
  }

  // Footer terms
  const footY = afterY + 90;
  doc.setDrawColor(226, 232, 240);
  doc.line(40, footY, pageWidth - 40, footY);
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.text(
    "Barang di atas dititipkan sebagai konsinyasi. Toko wajib melaporkan penjualan\n" +
    "secara berkala. Pembayaran dilakukan sesuai jumlah barang yang terjual.",
    40, footY + 15
  );

  // Signatures
  const sigY = footY + 60;
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(9);
  doc.text("Penitip (Project Hotto)", 60, sigY);
  doc.text("Penerima (" + (dist.store_name || "") + ")", pageWidth - 200, sigY);
  doc.line(40, sigY + 60, 200, sigY + 60);
  doc.line(pageWidth - 220, sigY + 60, pageWidth - 60, sigY + 60);

  const fileName = `Nota-Titipan-${(dist.store_name || "").replace(/\s+/g, "_")}-${(dist.id || "").slice(0, 8)}.pdf`;
  doc.save(fileName);
}


/**
 * Cetak Struk Kasir untuk penjualan langsung (80mm thermal look).
 */
export function printReceipt(sale) {
  const width = 226; // ~80mm
  const doc = new jsPDF({ unit: "pt", format: [width, 600] });
  let y = 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("PROJECT HOTTO", width / 2, y, { align: "center" }); y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("Terima kasih atas pembelian Anda", width / 2, y, { align: "center" }); y += 16;
  doc.setDrawColor(180); doc.setLineDashPattern([1, 1], 0);
  doc.line(10, y, width - 10, y); doc.setLineDashPattern([], 0); y += 12;

  doc.setFontSize(8);
  doc.text(`No: ${(sale.id || "").slice(0, 8).toUpperCase()}`, 10, y); y += 10;
  doc.text(`Tgl: ${formatDate(sale.date)}`, 10, y); y += 10;
  doc.text(`Konsumen: ${sale.customer_name || "-"}`, 10, y); y += 10;
  doc.text(`Kasir: ${sale.cashier_name || "-"}`, 10, y); y += 10;
  doc.text(`Bayar: ${sale.payment_method || "cash"}`, 10, y); y += 12;

  doc.setDrawColor(180); doc.setLineDashPattern([1, 1], 0);
  doc.line(10, y, width - 10, y); doc.setLineDashPattern([], 0); y += 12;

  doc.setFont("helvetica", "bold");
  doc.text("Item", 10, y); doc.text("Total", width - 10, y, { align: "right" }); y += 4;
  doc.setFont("helvetica", "normal"); y += 8;

  sale.items.forEach((it) => {
    doc.text(it.product_name.slice(0, 32), 10, y); y += 10;
    doc.text(`${it.qty} x ${formatIDR(it.unit_price)}`, 10, y);
    doc.text(formatIDR(it.subtotal), width - 10, y, { align: "right" });
    y += 12;
  });

  doc.setDrawColor(180); doc.setLineDashPattern([1, 1], 0);
  doc.line(10, y, width - 10, y); doc.setLineDashPattern([], 0); y += 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("TOTAL", 10, y);
  doc.text(formatIDR(sale.total), width - 10, y, { align: "right" });
  y += 20;

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.text("Barang yang sudah dibeli tidak dapat dikembalikan.", width / 2, y, { align: "center" }); y += 10;
  doc.text("~ Sampai jumpa lagi ~", width / 2, y, { align: "center" });

  doc.save(`Struk-${(sale.id || "").slice(0, 8)}.pdf`);
}


/**
 * Cetak Surat Jalan untuk pengiriman/penyaluran ke toko.
 * dist: { id, store_name, store_address?, date, items, total, notes, driver?, vehicle? }
 * opts: { driver, vehicle, city, address, products? }  // products optional: fallback lookup [{id, unit, sku}]
 */
export function printDeliveryOrder(dist, opts = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  const productMap = {};
  (opts.products || []).forEach((p) => { productMap[p.id] = p; });
  const resolveUnit = (it) => it.unit || productMap[it.product_id]?.unit || "pcs";
  const resolveSku = (it) => it.sku || productMap[it.product_id]?.sku || "-";

  // Header
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageWidth, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("SURAT JALAN", 40, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Delivery Order", 40, 58);

  doc.setFontSize(10);
  doc.text(`No: SJ-${(dist.id || "").slice(0, 8).toUpperCase()}`, pageWidth - 40, 40, { align: "right" });
  doc.text(`Tanggal: ${formatDate(dist.date)}`, pageWidth - 40, 58, { align: "right" });

  // Boxes: pengirim, penerima
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("PENGIRIM", 40, 110);
  doc.text("PENERIMA", pageWidth / 2 + 10, 110);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Project Hotto", 40, 128);
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text("Sistem Pencatatan Stok & Kas", 40, 142);

  doc.setTextColor(15, 23, 42);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(dist.store_name || "-", pageWidth / 2 + 10, 128);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const addr = opts.address || dist.store_address || "-";
  const addrLines = doc.splitTextToSize(addr, pageWidth / 2 - 60);
  doc.text(addrLines, pageWidth / 2 + 10, 142);

  // Info kurir
  const infoY = 180;
  doc.setDrawColor(226, 232, 240);
  doc.rect(40, infoY, pageWidth - 80, 40);
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.text("KURIR / DRIVER", 50, infoY + 14);
  doc.text("KENDARAAN", 200, infoY + 14);
  doc.text("TUJUAN KOTA", 360, infoY + 14);
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(10);
  doc.text(opts.driver || "-", 50, infoY + 30);
  doc.text(opts.vehicle || "-", 200, infoY + 30);
  doc.text(opts.city || "-", 360, infoY + 30);

  // Items table
  autoTable(doc, {
    startY: infoY + 55,
    head: [["#", "Kode", "Nama Barang", "Satuan", "Jumlah"]],
    body: dist.items.map((it, i) => {
      const unit = resolveUnit(it);
      return [
        i + 1,
        resolveSku(it),
        it.product_name,
        unit,
        `${it.qty} ${unit}${it.carton_qty ? ` (${it.carton_qty} karton)` : ""}`,
      ];
    }),
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontSize: 10, fontStyle: "bold" },
    bodyStyles: { fontSize: 10, textColor: [30, 41, 59] },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      1: { cellWidth: 80 },
      3: { cellWidth: 90 },
      4: { halign: "right", cellWidth: 110, fontStyle: "bold" },
    },
    margin: { left: 40, right: 40 },
    theme: "grid",
  });

  const afterY = doc.lastAutoTable.finalY + 20;

  // Notes
  if (dist.notes) {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.text("Catatan Pengiriman:", 40, afterY);
    doc.setTextColor(30, 41, 59);
    doc.text(doc.splitTextToSize(dist.notes, pageWidth - 80), 40, afterY + 14);
  }

  // Signatures
  const sigY = afterY + 80;
  const w = (pageWidth - 80) / 3;
  const labels = ["Pengirim", "Pengemudi", "Penerima"];
  labels.forEach((lb, i) => {
    const x = 40 + w * i;
    doc.setDrawColor(15, 23, 42);
    doc.line(x + 10, sigY + 60, x + w - 10, sigY + 60);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
    doc.text(lb, x + w / 2, sigY + 78, { align: "center" });
  });

  // Footer
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.text("Dokumen ini adalah bukti sah pengiriman barang. Simpan dengan baik.",
    pageWidth / 2, doc.internal.pageSize.getHeight() - 30, { align: "center" });

  doc.save(`Surat-Jalan-${(dist.store_name || "").replace(/\s+/g, "_")}-${(dist.id || "").slice(0, 8)}.pdf`);
}
